import { useMutation, useQuery } from '@apollo/client/react'
import {
  Button,
  CollapsibleSection,
  ConfirmationDialog,
  DataTable,
  ErrorState,
  FailureNotice,
  FOCUS_RING,
  InlineConfirmation,
  LoadingState,
  Notice,
  Section,
  Select,
  Tag,
  TextInput,
  cn,
  useLanguage,
  usePageTitle,
  useTranslate,
  type DataTableColumn,
} from '@athena/ui'
import { formatNumber } from '@athena/i18n'
import { PLANS, PLAN_CODES } from '@athena/shared'
import { useParams } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'

import { ReasonField } from '../features/management/ManagementSupport.js'
import {
  UserAccess,
  UserDangerZone,
  UserManagement,
} from '../features/management/UserManagement.js'
import { ResourceAuditLog } from '../features/management/ResourceAuditLog.js'
import { UserFeatureFlags } from '../features/management/UserFeatureFlags.js'
import { AccountStatusTag, InvitationTag, PlanTag, RoleTag } from '../features/display/Tags.js'
import {
  AdminAccountDocument,
  AdminAdjustAiUsageDocument,
  AdminSetStudentLimitDocument,
  AdminAssignPlanDocument,
  AdminEndPaidAccessDocument,
  AdminSchedulePlanChangeDocument,
  ManagedUserDocument,
  type AdminAccountQuery,
} from '../graphql/generated/operations.js'
import {
  countOf,
  creditsText,
  formatDate,
  formatDateRange,
  formatDateTime,
  formatDay,
  languageName,
  planName,
  suspensionReasonName,
  usageKindName,
  usageStatusName,
  type Role,
} from '../lib/display.js'
import { useFirstUserErrorText } from '../lib/user-errors.js'
import { useFocusFirstInvalid } from '../lib/validation.js'

type Subscription = NonNullable<AdminAccountQuery['adminSubscription']>
type UsageEntry = NonNullable<AdminAccountQuery['adminAiUsage']>[number]
type Relationship = NonNullable<AdminAccountQuery['adminRelationships']>[number]
/** Each field's message, or nothing; cleared by setting it back to undefined. */
type FieldErrors<K extends string> = { [Key in K]?: string | undefined }

/**
 * One account, organised around the questions an administrator arrives with.
 *
 * Who is this, and what state are they in — answered by the header before
 * anything is scrolled: name, address, profiles, access and plan. Then the
 * page runs from routine to consequential: the account's own details, its
 * access, its subscription and what it has spent, its students, its history,
 * and last, apart from everything, the one thing here that cannot be undone.
 *
 * Sections are a heading and a divider rather than a rounded panel each. The
 * page was a stack of white boxes with boxes inside them; the grouping was
 * never the border's doing.
 *
 * ── Stored beside effective ──────────────────────────────────────────────
 *
 * The subscription shows the plan in force and, only when they differ, what
 * the column says and which limits have been applied — because the commonest
 * support question is why they differ: a scheduled downgrade that fell due,
 * paid access that ran out, or enforcement not yet observed.
 *
 * ── Every write needs a reason ───────────────────────────────────────────
 *
 * The reason field is required by the schema and by these forms, and a
 * missing one is said in words under the field, with focus moved to it. The
 * value of an audit trail is the "why".
 *
 * ── Nothing here is a payment ────────────────────────────────────────────
 *
 * Assigning a plan records an arrangement made outside Athena. That is why
 * "paid up to" matters: without a date, an admin-granted paid plan would never
 * end, and the first thing to go wrong with manual billing is that somebody
 * stops paying and nothing notices.
 */
export function AccountScreen() {
  const t = useTranslate()
  const language = useLanguage()
  const { userId } = useParams({ from: '/$language/accounts/$userId' })

  /**
   * One round trip for the whole screen.
   *
   * The tutor-only half is skipped when the account has no Tutor profile — a
   * student has no subscription, and asking for one would be asking the server
   * for a row that does not exist.
   */
  const first = useQuery(AdminAccountDocument, {
    variables: { userId, tutorId: userId, skipTutor: true },
  })
  const tutorId = first.data?.adminUser.tutorId ?? null
  const account = useQuery(AdminAccountDocument, {
    variables: { userId, tutorId: tutorId ?? userId, skipTutor: tutorId === null },
    skip: !first.data,
  })
  // Access state lives on the managed record, which the profile form reads
  // too; Apollo shares the one request between them.
  const managed = useQuery(ManagedUserDocument, {
    variables: { userId },
    fetchPolicy: 'cache-and-network',
  }).data?.adminManagedUser

  const loaded = account.data ?? first.data
  const user = loaded?.adminUser
  const name = user ? (user.tutorDisplayName ?? user.studentDisplayName ?? user.name) : null
  usePageTitle(name || user?.email || t('admin.accountPageTitle'))

  if ((!first.data && first.loading) || (!account.data && account.loading))
    return <LoadingState>{t('common.loading')}</LoadingState>
  if (first.error || account.error) return <ErrorState>{t('admin.loadError')}</ErrorState>
  if (!loaded || !user) return null

  const subscription = loaded.adminSubscription ?? null
  const roles: Role[] = [
    ...(user.tutorId ? (['TUTOR'] as const) : []),
    ...(user.studentId ? (['STUDENT'] as const) : []),
    ...(user.isAdmin ? (['ADMIN'] as const) : []),
  ]

  const sections = [
    { id: 'user-management', label: t('management.editUser') },
    { id: 'access', label: t('admin.sectionAccess') },
    { id: 'feature-flags', label: t('management.featureFlags') },
    ...(subscription
      ? [
          { id: 'subscription', label: t('admin.subscriptionTitle') },
          { id: 'manage', label: t('admin.manageTitle') },
          { id: 'ai-usage', label: t('admin.aiTitle') },
          { id: 'relationships', label: t('admin.relationshipsTitle') },
        ]
      : []),
    { id: 'account-audit', label: t('admin.auditTitle') },
    { id: 'danger-zone', label: t('management.dangerZone') },
  ]

  return (
    <div className="mx-auto flex w-full max-w-5xl min-w-0 flex-col gap-6">
      <header className="flex min-w-0 flex-col gap-3">
        <div className="min-w-0">
          {/* Focusable from script only: a navigation lands here (see
              `RouteOrientation`). A place, not a control. */}
          <h1 tabIndex={-1} className="break-words text-h2 font-semibold text-text outline-none">
            {name || user.email}
          </h1>
          {name ? <p className="break-all text-body text-text-secondary">{user.email}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {roles.map((role) => (
            <RoleTag key={role} role={role} />
          ))}
          {managed ? <AccountStatusTag disabled={managed.disabled} /> : null}
          {managed?.pendingInvitation ? <InvitationTag /> : null}
          {subscription ? <PlanTag code={subscription.effectivePlanCode} /> : null}
          {managed && !managed.emailVerified ? (
            <Tag tone="neutral">{t('admin.emailUnverified')}</Tag>
          ) : null}
        </div>
        <p className="text-body-sm text-text-secondary">
          {t('admin.accountCreated', [formatDate(user.createdAt, language)])} ·{' '}
          {languageName(t, user.preferredLanguage)}
        </p>
        <TechnicalDetails
          rows={[
            [t('admin.techUserId'), user.id],
            ...(user.tutorId ? [[t('admin.techTutorId'), user.tutorId] as const] : []),
            ...(user.studentId ? [[t('admin.techStudentId'), user.studentId] as const] : []),
            ...(subscription
              ? [
                  [
                    t('admin.techPlanCodes'),
                    `${subscription.storedPlanCode} · ${subscription.effectivePlanCode} · ${
                      subscription.enforcedPlanCode ?? '—'
                    }`,
                  ] as const,
                ]
              : []),
          ]}
        />
      </header>

      {/* Chips rather than a second sidebar: one row of places on a long page,
          with the sticky header's height kept clear by `scroll-padding-top`. */}
      <nav aria-label={t('admin.accountSections')} className="flex flex-wrap gap-1.5">
        {sections.map((section) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            className={cn(
              'inline-flex h-control-sm items-center rounded-full border border-border bg-surface px-3 text-body-sm font-medium text-text-secondary',
              'hover:border-border-strong hover:text-text max-md:h-touch-target-min',
              FOCUS_RING,
            )}
          >
            {section.label}
          </a>
        ))}
      </nav>

      <UserManagement userId={userId} />
      <UserAccess userId={userId} />
      <UserFeatureFlags userId={userId} />

      {subscription ? (
        <>
          <SubscriptionSection subscription={subscription} />
          <PlanChangeSection subscription={subscription} />
          <StudentLimitSection key={subscription.tutorId} subscription={subscription} />
          <AiUsageSection subscription={subscription} usage={loaded.adminAiUsage ?? []} />
          <RelationshipsSection relationships={loaded.adminRelationships ?? []} />
        </>
      ) : user.studentId ? null : (
        <Notice title={t('admin.noSubscriptionTitle')}>{t('admin.noSubscriptionBody')}</Notice>
      )}

      <div id="account-audit" className="border-t border-border pt-6">
        <CollapsibleSection headingLevel={2} title={t('admin.auditTitle')}>
          <ResourceAuditLog resourceType="User" resourceId={user.id} />
        </CollapsibleSection>
      </div>

      <UserDangerZone userId={userId} />
    </div>
  )
}

/** A section of the account page: a heading and a divider, not a panel. */
function AccountSection({
  id,
  title,
  children,
}: {
  id: string
  title: string
  children: ReactNode
}) {
  return (
    <Section id={id} title={title} className="border-t border-border pt-6">
      {children}
    </Section>
  )
}

/**
 * Record ids and raw codes, for the administrator who needs to quote one to a
 * developer. Behind a deliberate click, because nobody else reads them.
 */
function TechnicalDetails({ rows }: { rows: readonly (readonly [string, string])[] }) {
  const t = useTranslate()
  return (
    <details className="text-caption">
      <summary
        className={cn(
          'w-fit cursor-pointer rounded-sm font-semibold text-primary underline-offset-2 hover:underline',
          // A caption-sized line is a 17px target. Padding, given back in
          // margin, makes it a 41px one without moving the page — and keeps
          // the summary a list item, so its disclosure marker stays.
          'py-3 -my-3',
          FOCUS_RING,
        )}
      >
        {t('admin.technicalDetails')}
      </summary>
      <dl className="mt-2 grid gap-x-4 gap-y-1 rounded-sm bg-surface-secondary p-3 sm:grid-cols-[max-content_minmax(0,1fr)]">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="font-semibold text-text-secondary">{label}</dt>
            <dd className="mb-1 break-all font-mono text-text sm:mb-0">{value}</dd>
          </div>
        ))}
      </dl>
    </details>
  )
}

/** One fact in a definition grid: a small label over its value. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-caption font-semibold text-text-secondary">{label}</dt>
      <dd className="min-w-0 break-words text-body-sm text-text">{children}</dd>
    </div>
  )
}

/** The pricing page's name for each paid feature the plan includes, in its order. */
function includedFeatures(features: Subscription['features']) {
  return [
    ...(features.tutorConfigurableAttempts ? ['marketing.pricing.multipleAttempts' as const] : []),
    ...(features.timedPractices ? ['marketing.pricing.timed' as const] : []),
    ...(features.availabilityWindow ? ['marketing.pricing.availability' as const] : []),
    ...(features.completeProgressTracking ? ['marketing.pricing.progressTracking' as const] : []),
    ...(features.detailedAttemptHistory ? ['marketing.pricing.attemptHistory' as const] : []),
  ]
}

function SubscriptionSection({ subscription }: { subscription: Subscription }) {
  const t = useTranslate()
  const language = useLanguage()
  const differs = subscription.storedPlanCode !== subscription.effectivePlanCode
  const unenforced = subscription.enforcedPlanCode !== subscription.effectivePlanCode

  function capacity(value: Subscription['students']) {
    return (
      <>
        {t('admin.capacityValue', [
          formatNumber(value.used, language),
          value.limit === null ? t('subscription.unlimited') : formatNumber(value.limit, language),
        ])}
        {value.overBy > 0 ? (
          <span className="block font-semibold text-error-text">
            {t('admin.capacityOver', [formatNumber(value.overBy, language)])}
          </span>
        ) : null}
      </>
    )
  }

  return (
    <AccountSection id="subscription" title={t('admin.subscriptionTitle')}>
      {subscription.lapsed ? (
        <Notice tone="warning" title={t('admin.lapsedTitle')}>
          {t('admin.lapsedBody', [formatDate(subscription.accessEndsAt, language)])}
        </Notice>
      ) : null}

      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 desktop:grid-cols-3">
        <Fact label={t('admin.planInForce')}>
          <span className="flex flex-wrap items-center gap-2">
            <PlanTag code={subscription.effectivePlanCode} />
            <span className="text-text-secondary">
              {t('admin.since', [formatDate(subscription.since, language)])}
            </span>
          </span>
        </Fact>
        {/* Stored and applied only when they differ from what is in force:
            the difference is the news, and the same plan three times is not. */}
        {differs ? (
          <Fact label={t('admin.storedPlan')}>{planName(t, subscription.storedPlanCode)}</Fact>
        ) : null}
        {unenforced ? (
          <Fact label={t('admin.enforcedPlan')}>
            {subscription.enforcedPlanCode ? planName(t, subscription.enforcedPlanCode) : '—'}
            <span className="block text-text-secondary">{t('admin.enforcementPending')}</span>
          </Fact>
        ) : null}
        <Fact label={t('admin.accessEnds')}>
          {subscription.accessEndsAt
            ? formatDate(subscription.accessEndsAt, language)
            : t('admin.noEndDate')}
        </Fact>
        <Fact label={t('admin.scheduled')}>
          {subscription.scheduledPlanCode
            ? t('admin.scheduledValue', [
                planName(t, subscription.scheduledPlanCode),
                formatDate(subscription.scheduledEffectiveAt, language),
              ])
            : t('admin.noScheduledChange')}
          {/* Whose decision it is, because that decides whether the tutor can
              call it off (SECURITY_AUDIT.md SEC-004). */}
          {subscription.scheduledBy ? (
            <span className="block text-text-secondary">
              {t(
                subscription.scheduledBy === 'ADMIN'
                  ? 'admin.scheduledByAdmin'
                  : 'admin.scheduledByTutor',
              )}
            </span>
          ) : null}
        </Fact>
        <Fact label={t('admin.period')}>
          {subscription.periodStartsAt && subscription.periodEndsAt
            ? formatDateRange(subscription.periodStartsAt, subscription.periodEndsAt, language)
            : '—'}
        </Fact>
        {/* The paid features the plan in force includes, as the server applies
            them — named the way the pricing page names them. */}
        <Fact label={t('admin.includedFeatures')}>
          {includedFeatures(subscription.features).length > 0
            ? includedFeatures(subscription.features)
                .map((key) => t(key))
                .join(' · ')
            : t('admin.noPaidFeatures')}
        </Fact>
        <Fact label={t('admin.students')}>{capacity(subscription.students)}</Fact>
        <Fact label={t('admin.practices')}>{capacity(subscription.practices)}</Fact>
        <Fact label={t('admin.aiCredits')}>
          {t('admin.aiCreditsValue', [
            formatNumber(subscription.ai.consumed, language),
            formatNumber(subscription.ai.allowance, language),
          ])}
          {subscription.ai.reserved > 0 ? (
            <span className="block text-text-secondary">
              {countOf(
                t,
                subscription.ai.reserved,
                'admin.aiCreditsHeldOne',
                'admin.aiCreditsHeldOther',
              )}
            </span>
          ) : null}
        </Fact>
      </dl>

      {subscription.blockedPractices.length > 0 ? (
        <Notice tone="warning" title={t('admin.blockedTitle')}>
          <ul>
            {subscription.blockedPractices.map((practice) => (
              <li key={practice.practiceId}>
                {practice.title} ·{' '}
                {countOf(t, practice.questionCount, 'count.questionsOne', 'count.questionsOther')}
                {practice.timedWithoutPlan ? ` · ${t('admin.blockedTimed')}` : ''}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}
    </AccountSection>
  )
}

function PlanChangeSection({ subscription }: { subscription: Subscription }) {
  const t = useTranslate()
  const language = useLanguage()
  const run = useRunAdminChange()
  const [plan, setPlan] = useState('')
  const [date, setDate] = useState('')
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<FieldErrors<'plan' | 'date' | 'reason'>>({})
  const [outcome, setOutcome] = useState<Outcome>()
  /** Ending paid access, which is asked first: it takes capacity away. */
  const [endingAccess, setEndingAccess] = useState(false)
  const [endOutcome, setEndOutcome] = useState<Outcome>()
  const invalid = useFocusFirstInvalid<HTMLFormElement>()

  const refetch = { refetchQueries: [AdminAccountDocument] }
  const [assignPlan, assigning] = useMutation(AdminAssignPlanDocument, refetch)
  const [scheduleChange, scheduling] = useMutation(AdminSchedulePlanChangeDocument, refetch)
  const [endAccess, ending] = useMutation(AdminEndPaidAccessDocument, refetch)

  /** What this action needs, said under each field that lacks it. */
  function check(needs: { plan?: boolean; date?: boolean }): boolean {
    const next = {
      ...(needs.plan && plan === '' ? { plan: t('management.errors.plan') } : {}),
      ...(needs.date && date === '' ? { date: t('management.errors.endDate') } : {}),
      ...(reason.trim() === '' ? { reason: t('management.errors.reason') } : {}),
    }
    setErrors(next)
    const valid = Object.keys(next).length === 0
    if (!valid) invalid.request()
    return valid
  }

  return (
    <AccountSection id="manage" title={t('admin.manageTitle')}>
      <p className="max-w-prose text-body-sm text-text-secondary">{t('admin.manageBody')}</p>

      <form
        ref={invalid.ref}
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start">
          <div className="w-full sm:w-56">
            <Select
              label={t('admin.planLabel')}
              placeholder={t('admin.planPlaceholder')}
              value={plan}
              onValueChange={(value) => {
                setPlan(value)
                setErrors((current) => ({ ...current, plan: undefined }))
              }}
              options={PLAN_CODES.map((code) => ({ value: code, label: planName(t, code) }))}
              {...(errors.plan ? { error: errors.plan } : {})}
            />
          </div>
          <div className="w-full sm:w-56">
            <TextInput
              label={t('admin.accessEndsLabel')}
              helperText={t('admin.accessEndsHelper')}
              type="date"
              value={date}
              onChange={(event) => {
                setDate(event.target.value)
                setErrors((current) => ({ ...current, date: undefined }))
              }}
              error={errors.date ?? ''}
            />
          </div>
        </div>

        <ReasonField
          value={reason}
          onChange={(value) => {
            setReason(value)
            setErrors((current) => ({ ...current, reason: undefined }))
          }}
          error={errors.reason ?? ''}
        />

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              loading={assigning.loading}
              loadingText={t('common.saving')}
              onClick={() => {
                if (!check({ plan: true })) return
                void run(
                  () =>
                    assignPlan({
                      variables: {
                        tutorId: subscription.tutorId,
                        planCode: plan as never,
                        accessEndsAt: date === '' ? null : date,
                        reason: reason.trim(),
                      },
                    }).then((result) => result.data?.adminAssignPlan.userErrors ?? null),
                  setOutcome,
                  { retryable: true },
                )
              }}
            >
              {t('admin.assignPlan')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              loading={scheduling.loading}
              loadingText={t('common.saving')}
              onClick={() => {
                if (!check({ plan: true })) return
                void run(
                  () =>
                    scheduleChange({
                      variables: {
                        tutorId: subscription.tutorId,
                        planCode: plan as never,
                        effectiveAt: date === '' ? null : date,
                        reason: reason.trim(),
                      },
                    }).then((result) => result.data?.adminSchedulePlanChange.userErrors ?? null),
                  setOutcome,
                  { retryable: true },
                )
              }}
            >
              {t('admin.scheduleChange')}
            </Button>
          </div>
          <p className="max-w-prose text-caption text-text-secondary">
            {t('admin.planActionsRoutine')}
          </p>
        </div>

        <OutcomeMessage
          outcome={outcome}
          failedTitle={t('admin.planNotChanged')}
          retrying={assigning.loading || scheduling.loading || ending.loading}
        />

        {/* Apart from the routine two, under its own heading: ending paid
            access takes capacity from a tutor and their students, so it is not
            a third way of finishing the same form. */}
        <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h3 className="text-body-sm font-semibold text-text">{t('admin.endAccessHeading')}</h3>
            <p className="max-w-prose text-body-sm text-text-secondary">
              {t('admin.endAccessHelp')}
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            className="shrink-0"
            onClick={() => {
              if (!check({ date: true })) return
              setOutcome(undefined)
              setEndOutcome(undefined)
              setEndingAccess(true)
            }}
          >
            {t('admin.endAccess')}
          </Button>
        </div>
      </form>

      {/* Asked first, unlike assigning or scheduling: from the date, the
          account is on Free and whatever does not fit is suspended.
          Recoverable — nothing is deleted — but it takes capacity from a tutor
          and their students, so it is red, with keeping paid access
          recommended. Free's limits are read from the catalog, never written
          here. */}
      <ConfirmationDialog
        open={endingAccess}
        onOpenChange={setEndingAccess}
        intent="destructive"
        title={t('admin.endAccessTitle', [formatDay(date, language)])}
        description={t('admin.endAccessBody', [
          formatDay(date, language),
          String(PLANS.FREE.maxStudents),
          String(PLANS.FREE.maxActivePractices),
          String(PLANS.FREE.maxQuestionsPerPractice),
        ])}
        confirmLabel={t('admin.endAccess')}
        confirmingLabel={t('common.saving')}
        cancelLabel={t('admin.keepPaidAccess')}
        failure={
          // Pressing "End paid access" again is the retry.
          <OutcomeMessage
            outcome={endOutcome}
            failedTitle={t('admin.planNotChanged')}
            retrying={ending.loading}
          />
        }
        onConfirm={async () => {
          const saved = await run(
            () =>
              endAccess({
                variables: {
                  tutorId: subscription.tutorId,
                  accessEndsAt: date,
                  reason: reason.trim(),
                },
              }).then((result) => result.data?.adminEndPaidAccess.userErrors ?? null),
            setEndOutcome,
            { retryable: false },
          )
          if (saved) setOutcome({ kind: 'saved' })
          return saved
        }}
      />
    </AccountSection>
  )
}

function StudentLimitSection({ subscription }: { subscription: Subscription }) {
  const t = useTranslate()
  const language = useLanguage()
  const run = useRunAdminChange()
  const [limit, setLimit] = useState(subscription.studentLimitOverride?.toString() ?? '')
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<FieldErrors<'limit' | 'reason'>>({})
  const [outcome, setOutcome] = useState<Outcome>()
  const invalid = useFocusFirstInvalid<HTMLFormElement>()
  const [save, saving] = useMutation(AdminSetStudentLimitDocument, {
    refetchQueries: [AdminAccountDocument],
    awaitRefetchQueries: true,
  })
  const paid = subscription.effectivePlanCode !== 'FREE'
  return (
    <AccountSection id="student-limit" title={t('admin.studentLimitTitle')}>
      <p className="max-w-prose text-body-sm text-text-secondary">
        {t('admin.studentLimitBody', [
          formatNumber(PLANS[subscription.effectivePlanCode].maxStudents, language),
        ])}
      </p>
      <form
        ref={invalid.ref}
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          const studentLimit = limit.trim() === '' ? null : Number(limit)
          const next = {
            ...(studentLimit !== null &&
            (!Number.isInteger(studentLimit) || studentLimit < 1 || studentLimit > 2147483647)
              ? { limit: t('admin.studentLimitInvalid') }
              : {}),
            ...(reason.trim() === '' ? { reason: t('management.errors.reason') } : {}),
          }
          setErrors(next)
          if (Object.keys(next).length) {
            invalid.request()
            return
          }
          void run(
            () =>
              save({
                variables: { tutorId: subscription.tutorId, studentLimit, reason: reason.trim() },
              }).then((result) => result.data?.adminSetStudentLimit.userErrors ?? null),
            setOutcome,
            { retryable: false },
          ).then((saved) => {
            if (saved) setReason('')
          })
        }}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="w-full sm:w-56">
            <TextInput
              label={t('admin.studentLimitLabel')}
              type="number"
              min={1}
              step={1}
              value={limit}
              disabled={!paid && subscription.studentLimitOverride == null}
              onChange={(event) => {
                setLimit(event.target.value)
                setErrors((current) => ({ ...current, limit: undefined }))
                setOutcome(undefined)
              }}
              error={errors.limit ?? ''}
            />
          </div>
          <ReasonField
            value={reason}
            onChange={(value) => {
              setReason(value)
              setErrors((current) => ({ ...current, reason: undefined }))
            }}
            error={errors.reason ?? ''}
          />
        </div>
        {!paid && <p className="text-caption text-text-secondary">{t('admin.studentLimitFree')}</p>}
        <div>
          <Button
            type="submit"
            variant="secondary"
            className="w-full sm:w-auto"
            disabled={!paid && (subscription.studentLimitOverride == null || limit.trim() !== '')}
            loading={saving.loading}
            loadingText={t('common.saving')}
          >
            {t('admin.studentLimitSave')}
          </Button>
        </div>
        <OutcomeMessage
          outcome={outcome}
          failedTitle={t('admin.studentLimitUnconfirmed')}
          retrying={saving.loading}
        />
      </form>
    </AccountSection>
  )
}

function AiUsageSection({
  subscription,
  usage,
}: {
  subscription: Subscription
  usage: readonly UsageEntry[]
}) {
  const t = useTranslate()
  const language = useLanguage()
  const run = useRunAdminChange()
  const [adjustment, setAdjustment] = useState('')
  const [reason, setReason] = useState('')
  const [errors, setErrors] = useState<FieldErrors<'adjustment' | 'reason'>>({})
  const [outcome, setOutcome] = useState<Outcome>()
  const invalid = useFocusFirstInvalid<HTMLFormElement>()
  const [adjust, adjusting] = useMutation(AdminAdjustAiUsageDocument, {
    refetchQueries: [AdminAccountDocument],
  })

  const columns: DataTableColumn<UsageEntry>[] = [
    {
      id: 'when',
      header: t('admin.columnWhen'),
      className: 'whitespace-nowrap',
      cell: (entry) => formatDateTime(entry.reservedAt, language),
    },
    {
      id: 'event',
      header: t('admin.columnEvent'),
      cell: (entry) => usageKindName(t, entry.kind),
    },
    {
      id: 'credits',
      header: t('admin.columnCredits'),
      className: 'whitespace-nowrap',
      cell: (entry) => creditsText(t, entry.kind, entry.amount),
    },
    {
      id: 'status',
      header: t('admin.columnStatus'),
      cell: (entry) => usageStatusName(t, entry.status, entry.failed),
    },
    {
      id: 'note',
      header: t('admin.columnNote'),
      priority: 'secondary',
      cell: (entry) => <span className="text-text-secondary">{entry.note ?? '—'}</span>,
    },
  ]

  return (
    <AccountSection id="ai-usage" title={t('admin.aiTitle')}>
      <p className="text-body-sm text-text">
        {t('admin.aiCreditsValue', [
          formatNumber(subscription.ai.consumed, language),
          formatNumber(subscription.ai.allowance, language),
        ])}
        {subscription.ai.reserved > 0
          ? ` · ${countOf(t, subscription.ai.reserved, 'admin.aiCreditsHeldOne', 'admin.aiCreditsHeldOther')}`
          : ''}
      </p>

      {usage.length === 0 ? (
        <p className="text-body-sm text-text-secondary">{t('admin.aiHistoryEmpty')}</p>
      ) : (
        <DataTable
          caption={t('admin.aiHistoryCaption')}
          columns={columns}
          rows={usage}
          rowKey={(entry) => entry.id}
          rowTone={(entry) => (entry.status === 'RELEASED' ? 'muted' : 'default')}
        />
      )}

      <form
        ref={invalid.ref}
        noValidate
        className="flex flex-col gap-4 border-t border-border pt-4"
        onSubmit={(event) => {
          event.preventDefault()
          const amount = Number(adjustment)
          const next = {
            ...(adjustment.trim() === '' || !Number.isInteger(amount) || amount === 0
              ? { adjustment: t('management.errors.adjustment') }
              : {}),
            ...(reason.trim() === '' ? { reason: t('management.errors.reason') } : {}),
          }
          setErrors(next)
          if (Object.keys(next).length > 0) {
            invalid.request()
            return
          }
          void run(
            () =>
              adjust({
                variables: { tutorId: subscription.tutorId, amount, reason: reason.trim() },
              }).then((result) => result.data?.adminAdjustAiUsage.userErrors ?? null),
            setOutcome,
            // Not retried: an adjustment is a new ledger row, so a second send
            // after a lost answer would count it twice.
            { retryable: false },
          ).then((saved) => {
            if (saved) {
              setAdjustment('')
              setReason('')
            }
          })
        }}
      >
        <div>
          <h3 className="text-body-sm font-semibold text-text">{t('admin.aiAdjustmentTitle')}</h3>
          <p className="max-w-prose text-body-sm text-text-secondary">{t('admin.aiBody')}</p>
        </div>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="w-full sm:w-40">
            <TextInput
              label={t('admin.adjustmentLabel')}
              type="number"
              step={1}
              value={adjustment}
              onChange={(event) => {
                setAdjustment(event.target.value)
                setErrors((current) => ({ ...current, adjustment: undefined }))
              }}
              error={errors.adjustment ?? ''}
            />
          </div>
          <ReasonField
            value={reason}
            onChange={(value) => {
              setReason(value)
              setErrors((current) => ({ ...current, reason: undefined }))
            }}
            error={errors.reason ?? ''}
          />
        </div>
        <p className="-mt-2 text-caption text-text-secondary">{t('admin.adjustmentHelper')}</p>
        <div>
          <Button
            type="submit"
            variant="secondary"
            className="w-full sm:w-auto"
            loading={adjusting.loading}
            loadingText={t('common.saving')}
          >
            {t('admin.adjust')}
          </Button>
        </div>
        <OutcomeMessage
          outcome={outcome}
          failedTitle={t('admin.adjustmentUnconfirmed')}
          retrying={adjusting.loading}
        />
      </form>
    </AccountSection>
  )
}

function RelationshipsSection({ relationships }: { relationships: readonly Relationship[] }) {
  const t = useTranslate()
  const language = useLanguage()
  const columns: DataTableColumn<Relationship>[] = [
    {
      id: 'student',
      header: t('admin.columnStudent'),
      cell: (row) => <span className="font-medium text-text">{row.displayName}</span>,
    },
    {
      id: 'email',
      header: t('admin.columnEmail'),
      cell: (row) => <span className="break-all">{row.email ?? '—'}</span>,
    },
    {
      id: 'connected',
      header: t('admin.columnConnected'),
      priority: 'secondary',
      className: 'whitespace-nowrap',
      cell: (row) => formatDate(row.connectedAt, language),
    },
    {
      id: 'status',
      header: t('admin.columnStatus'),
      cell: (row) =>
        row.suspendedAt ? (
          <Tag tone="muted" icon="⊘">
            {suspensionReasonName(t, row.suspendedReason)}
          </Tag>
        ) : !row.claimed ? (
          <Tag tone="brand" icon="◌">
            {t('admin.unclaimed')}
          </Tag>
        ) : (
          <Tag tone="positive" icon="●">
            {t('admin.enabled')}
          </Tag>
        ),
    },
  ]

  return (
    <AccountSection id="relationships" title={t('admin.relationshipsTitle')}>
      {relationships.length === 0 ? (
        <p className="text-body-sm text-text-secondary">{t('admin.relationshipsEmpty')}</p>
      ) : (
        <DataTable
          caption={t('admin.relationshipsCaption')}
          columns={columns}
          rows={relationships}
          rowKey={(row) => row.studentId}
          rowTone={(row) => (row.suspendedAt ? 'muted' : 'default')}
        />
      )}
    </AccountSection>
  )
}

/** One privileged change, resolved to the server's refusals, or `null` when no answer came back with it. */
type AdminCall = () => Promise<
  readonly { code: string; message: string; params: readonly string[] }[] | null
>

type Outcome =
  | { kind: 'saved' }
  | { kind: 'refused'; text: string }
  | { kind: 'failed'; error: unknown; retry?: () => void }

/**
 * Run one privileged change and record what it came to.
 *
 * Confirmed only when the server says so; a refusal in the server's words;
 * a request that never got an answer as a failure — with a retry only when
 * `retryable`, because not every one of these is safe to send twice. The
 * reason and every value stay in their fields either way.
 */
function useRunAdminChange() {
  const t = useTranslate()
  const userErrorText = useFirstUserErrorText()

  return async function run(
    call: AdminCall,
    setOutcome: (outcome: Outcome | undefined) => void,
    { retryable, retry = false }: { retryable: boolean; retry?: boolean },
  ): Promise<boolean> {
    // A retry keeps its failure on screen, saying "Trying again…", until the answer replaces it.
    if (!retry) setOutcome(undefined)
    let errors
    try {
      errors = await call()
    } catch (error) {
      setOutcome({
        kind: 'failed',
        error,
        ...(retryable
          ? { retry: () => void run(call, setOutcome, { retryable, retry: true }) }
          : {}),
      })
      return false
    }
    if (errors === null) {
      setOutcome({ kind: 'failed', error: new Error('The change came back without a result.') })
      return false
    }
    if (errors.length > 0) {
      setOutcome({ kind: 'refused', text: userErrorText(errors, t('common.tryAgain')) })
      return false
    }
    setOutcome({ kind: 'saved' })
    return true
  }
}

/** What a section's last change came to, beside the buttons that made it. */
function OutcomeMessage({
  outcome,
  failedTitle,
  retrying,
}: {
  outcome: Outcome | undefined
  failedTitle: string
  retrying: boolean
}) {
  const t = useTranslate()
  if (!outcome) return null
  if (outcome.kind === 'saved')
    return <InlineConfirmation>{t('management.saved')}</InlineConfirmation>
  if (outcome.kind === 'refused') return <Notice tone="error" title={outcome.text} />
  return (
    <FailureNotice
      title={failedTitle}
      error={outcome.error}
      retrying={retrying}
      {...(outcome.retry ? { onRetry: outcome.retry } : {})}
    />
  )
}
